import { useEffect, useMemo, useState } from 'react';
import {
  Controller,
  ControllerRenderProps,
  UseFormReturn,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';

import { DatePicker } from 'components/DatePicker';
import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Typography } from 'components/typography';
import { getActiveLtlIntegration } from 'hooks/useCustomers';
import { useServiceFeatures } from 'hooks/useServiceContext';
import {
  LTLCustomerStaff,
  getLTLCustomerStaff,
} from 'lib/api/getLTLCustomerStaff';
import { Maybe } from 'types/UtilityTypes';
import { IntegrationType, TMS } from 'types/enums/Integrations';

import { useQuickQuoteContext } from '../../context/QuickQuoteContext';
import { QuickQuoteInputs } from '../../types';
import { QuoteInformationSection as LTLQuoteInformationSection } from '../QuoteInformationSection';
import { getThreeGNumericTradingPartnerId } from './getThreeGNumericTradingPartnerId';
import { mapTradingPartnerCompatibilityProfilesToForm } from './mapTradingPartnerCompatibilityProfiles';

interface QuoteInformationSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
}

export function QuoteInformationSection({
  formMethods,
}: QuoteInformationSectionProps) {
  const {
    control,
    formState: { errors },
  } = formMethods;

  return (
    <LTLQuoteInformationSection
      formMethods={formMethods}
      showLTLStaff={false}
      afterCustomer={
        <>
          <ThreeGQuoteContactSection formMethods={formMethods} />
          <div className='w-full'>
            <Label name='quoteExpirationDate'>Quote Expiration Date</Label>
            <div className='mt-1'>
              <Controller
                name='quoteExpirationDate'
                control={control}
                render={({ field }: { field: ControllerRenderProps<any> }) => (
                  <DatePicker field={field} />
                )}
              />
            </div>
            <ErrorMessage
              errors={errors}
              name='quoteExpirationDate'
              render={({ message }: { message: string }) => (
                <Typography variant='body-xs' className='text-error-500'>
                  {message}
                </Typography>
              )}
            />
          </div>
        </>
      }
    />
  );
}

const ThreeGQuoteContactSection = ({
  formMethods,
}: {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [quoteContactList, setQuoteContactList] =
    useState<Maybe<LTLCustomerStaff[]>>(null);

  const { ltlCustomer, setLtlCustomerStaff } = useQuickQuoteContext();
  const { ltlTMSIntegrations, quotingIntegrations } = useServiceFeatures();
  const activeLtlIntegration = useMemo(
    () => getActiveLtlIntegration(ltlTMSIntegrations),
    [ltlTMSIntegrations]
  );
  const activeIntegrationId = activeLtlIntegration?.id;
  const activeIntegrationName = activeLtlIntegration?.name;
  const { setValue, watch } = formMethods;
  const quoteContactNameValue = watch('quoteContactName');

  useEffect(() => {
    if (!ltlCustomer) {
      setQuoteContactList(null);
      setValue('quoteContactName', '');
      setLtlCustomerStaff(null);
      setValue('ltl.compatibilityProfiles', []);
      return;
    }

    setValue('quoteContactName', '');
    setLtlCustomerStaff(null);
    setValue('ltl.compatibilityProfiles', []);

    const numericTradingPartnerId =
      getThreeGNumericTradingPartnerId(ltlCustomer);
    if (!numericTradingPartnerId) {
      // Wait for numeric TradingPartnerID; customerId alone may be TradingPartnerNum.
      setQuoteContactList(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const pricingIntegration = quotingIntegrations?.find((integration) => {
      return (
        integration.type === IntegrationType.Quoting &&
        integration.name === activeIntegrationName
      );
    });

    getLTLCustomerStaff(ltlCustomer.customerName, numericTradingPartnerId, {
      tmsIntegrationId: activeIntegrationId,
      pricingIntegrationId: pricingIntegration?.id,
      integrationName: activeIntegrationName,
      // Backend uses externalId for 3G compatibility-profile lookup.
      externalId: ltlCustomer.externalId ?? numericTradingPartnerId,
    }).then((result) => {
      if (cancelled) {
        return;
      }

      setIsLoading(false);

      if (result.isOk()) {
        setQuoteContactList(result.value.customerList);
        if (activeIntegrationName === TMS.ThreeG) {
          setValue(
            'ltl.compatibilityProfiles',
            mapTradingPartnerCompatibilityProfilesToForm(
              result.value.compatibilityProfiles
            )
          );
        }
      } else {
        setQuoteContactList(null);
        if (activeIntegrationName === TMS.ThreeG) {
          setValue('ltl.compatibilityProfiles', []);
        }
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    ltlCustomer,
    activeIntegrationId,
    activeIntegrationName,
    quotingIntegrations,
    setLtlCustomerStaff,
    setValue,
  ]);

  useEffect(() => {
    if (!quoteContactNameValue || !quoteContactList?.length) {
      return;
    }

    const staff = quoteContactList.find(
      (s) => String(s.ownerId) === String(quoteContactNameValue)
    );

    if (staff) {
      setLtlCustomerStaff({
        email: staff.email,
        ownerId: staff.ownerId,
        ownerName: staff.ownerName,
        tmsIntegrationId: staff.tmsIntegrationId,
      });
    } else {
      setLtlCustomerStaff(null);
    }
  }, [quoteContactNameValue, quoteContactList, setLtlCustomerStaff]);

  useEffect(() => {
    if (isLoading || quoteContactNameValue || !quoteContactList?.length) {
      return;
    }

    const staff = quoteContactList[0];
    setValue('quoteContactName', String(staff.ownerId));
  }, [isLoading, quoteContactNameValue, quoteContactList, setValue]);

  if (!ltlCustomer) {
    return null;
  }

  const quoteContactOptions = (quoteContactList ?? []).map((staff) => ({
    label: staff.ownerName,
    value: String(staff.ownerId),
  }));

  return (
    <div className='w-full'>
      <Label name='quoteContactName' className='mb-0'>
        Quote Contact
      </Label>
      <Select
        value={quoteContactNameValue ?? ''}
        onChange={(value) => {
          setValue('quoteContactName', value != null ? String(value) : '');
        }}
        options={quoteContactOptions}
        showSearch
        placeholder={
          isLoading ? 'Loading...' : 'Select or search quote contact'
        }
        disabled={isLoading}
        className='w-full mt-1'
      />
    </div>
  );
};
